import { ChartOptions } from 'chart.js';

export const pieChartOptions = {
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: {
        color: 'rgba(255, 255, 255, 0.8)',
        font: {
          size: window.innerWidth < 768 ? 10 : 12
        },
        padding: window.innerWidth < 768 ? 10 : 12
      }
    },
    tooltip: {
      callbacks: {
        label: (context: any) => {
          if (context.dataset.rawBalances) {
            const value = context.raw;
            const rawBalance = context.dataset.rawBalances[context.dataIndex];
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return [
              `τ balance: ${value.toFixed(4)} τ (${percentage}%)`,
              `α balance: ${rawBalance.toFixed(4)} α`
            ];
          } else {
            const value = context.raw;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${value.toFixed(4)} τ (${percentage}%)`;
          }
        }
      }
    }
  },
  maintainAspectRatio: false,
  layout: {
    padding: 20
  },
  canvas: {
    backgroundColor: '#121212'
  }
};

export const lineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index' as const,
    intersect: false,
  },
  scales: {
    x: {
      type: 'time' as const,
      time: {
        unit: 'day' as const,
        tooltipFormat: 'PPP',
        displayFormats: {
          day: 'MMM d'
        }
      },
      grid: {
        display: false
      },
      ticks: {
        color: 'rgba(255, 255, 255, 0.6)',
        font: {
          size: window.innerWidth < 768 ? 10 : 12
        },
        maxRotation: 0,
        padding: window.innerWidth < 768 ? 5 : 10,
        maxTicksLimit: 7 // Show all 7 days
      },
      border: {
        display: false
      }
    },
    y: {
      display: false, // Hide the y-axis
      grid: {
        display: false
      },
      border: {
        display: false
      }
    }
  },
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: 'rgba(255, 255, 255, 0.8)',
      bodyColor: 'rgba(255, 255, 255, 0.8)',
      padding: window.innerWidth < 768 ? 8 : 12,
      boxPadding: window.innerWidth < 768 ? 4 : 6,
      usePointStyle: true,
      callbacks: {
        title: (context: any) => {
          const date = new Date(context[0].parsed.x);
          return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
          });
        },
        label: (context: any) => {
          const value = context.raw;
          return `Balance: ${value.toFixed(4)} τ`;
        }
      }
    }
  },
  elements: {
    point: {
      radius: 0,
      hitRadius: window.innerWidth < 768 ? 20 : 30,
      hoverRadius: window.innerWidth < 768 ? 4 : 5,
      backgroundColor: 'rgba(34, 197, 94, 1)',
      borderColor: 'rgba(255, 255, 255, 1)',
      borderWidth: 2,
    },
    line: {
      tension: 0.4,
    }
  },
  layout: {
    padding: 20
  },
  canvas: {
    backgroundColor: '#121212'
  }
};

// Convert balance from Planck to TAO (1 TAO = 1e9 Planck)
const convertPlanckToTao = (planckBalance: string | number): number => {
  if (typeof planckBalance === 'number') {
    return planckBalance / 1e9;
  }
  try {
    return Number(BigInt(planckBalance)) / 1e9;
  } catch (err) {
    console.warn('Failed to convert balance to BigInt:', err);
    return parseFloat(planckBalance) / 1e9;
  }
};

export const formatBalance = (balance: string | number) => {
  const taoBalance = convertPlanckToTao(balance);
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4
  }).format(taoBalance);
};

export const formatUsdValue = (balance: string, price: number | null) => {
  if (!price) return 'Loading...';
  const taoBalance = convertPlanckToTao(balance);
  const usdValue = taoBalance * price;
  return `$${usdValue.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

export const formatUsdValueCompact = (value: string | number, price: number | null) => {
  if (!price) return 'Loading...';
  
  let numValue: number;
  if (typeof value === 'string') {
    numValue = convertPlanckToTao(value) * price;
  } else {
    numValue = value * price;
  }
  
  if (numValue >= 1e9) {
    return `${(numValue / 1e9).toFixed(1)}B`;
  } else if (numValue >= 1e6) {
    return `${(numValue / 1e6).toFixed(1)}M`;
  } else if (numValue >= 1e3) {
    return `${(numValue / 1e3).toFixed(1)}K`;
  } else {
    return numValue.toFixed(2);
  }
};

export const formatDate = (timestamp: string) => {
  return new Date(timestamp).toLocaleString();
};

export const truncateAddress = (address: string | undefined) => {
  if (!address) return 'Unknown';
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
};

// Download chart as PNG image with dark background and subnet profile
export const downloadChartAsPng = async (
  chartRef: any, 
  filename: string = 'chart.png', 
  options: {
    subnetName?: string;
    profileImageUrl?: string;
    netuid?: number;
  } = {}
) => {
  if (!chartRef || !chartRef.current) {
    console.error('Chart reference is not available');
    return false;
  }
  
  try {
    // Get the chart instance
    const chartInstance = chartRef.current;
    
    // Create a temporary canvas to render the chart with dark background
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    
    if (!tempCtx) {
      throw new Error('Failed to get canvas context');
    }
    
    // Set dimensions to match the original chart
    const originalCanvas = chartInstance.canvas;
    tempCanvas.width = originalCanvas.width;
    tempCanvas.height = originalCanvas.height;
    
    // Fill with dark background (similar to the app's background)
    tempCtx.fillStyle = '#121212';
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    
    // Draw the chart on the temporary canvas
    tempCtx.drawImage(originalCanvas, 0, 0);
    
    // Add subnet profile picture and name if provided
    if (options.subnetName || options.profileImageUrl) {
      const padding = 15;
      const imageSize = 40;
      
      // If profile image URL is provided, load and draw it
      if (options.profileImageUrl) {
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          
          // Wait for the image to load
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = options.profileImageUrl;
          });
          
          // Draw circular profile image
          tempCtx.save();
          tempCtx.beginPath();
          tempCtx.arc(padding + imageSize/2, padding + imageSize/2, imageSize/2, 0, Math.PI * 2, true);
          tempCtx.closePath();
          tempCtx.clip();
          
          // Draw the image
          tempCtx.drawImage(img, padding, padding, imageSize, imageSize);
          
          // Add a border
          tempCtx.strokeStyle = 'rgba(239, 68, 68, 1)';
          tempCtx.lineWidth = 2;
          tempCtx.stroke();
          
          tempCtx.restore();
        } catch (error) {
          console.error('Error loading subnet profile image:', error);
          // If image fails to load, draw a placeholder circle
          tempCtx.fillStyle = 'rgba(239, 68, 68, 0.8)';
          tempCtx.beginPath();
          tempCtx.arc(padding + imageSize/2, padding + imageSize/2, imageSize/2, 0, Math.PI * 2);
          tempCtx.fill();
        }
      } else {
        // Draw a placeholder circle if no image URL
        tempCtx.fillStyle = 'rgba(239, 68, 68, 0.8)';
        tempCtx.beginPath();
        tempCtx.arc(padding + imageSize/2, padding + imageSize/2, imageSize/2, 0, Math.PI * 2);
        tempCtx.fill();
      }
      
      // Draw subnet name
      if (options.subnetName) {
        tempCtx.font = 'bold 16px IBM Plex Sans, sans-serif';
        tempCtx.fillStyle = 'white';
        tempCtx.textBaseline = 'middle';
        
        // Position text next to the image
        const textX = padding + imageSize + 10;
        const textY = padding + imageSize/2;
        
        // Draw subnet name
        tempCtx.fillText(options.subnetName, textX, textY);
        
        // Draw netuid if available
        if (options.netuid !== undefined) {
          tempCtx.font = '12px IBM Plex Sans, sans-serif';
          tempCtx.fillStyle = 'rgba(255, 255, 255, 0.7)';
          tempCtx.fillText(`SN${options.netuid}`, textX, textY + 18);
        }
      }
    }
    
    // Add SwordScan watermark
    tempCtx.font = '12px IBM Plex Sans, sans-serif';
    tempCtx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    tempCtx.textAlign = 'right';
    tempCtx.fillText('SWORDSCAN.io', tempCanvas.width - padding, tempCanvas.height - padding);
    
    // Create a download link
    const link = document.createElement('a');
    link.download = filename;
    link.href = tempCanvas.toDataURL('image/png');
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return true;
  } catch (error) {
    console.error('Error downloading chart:', error);
    return false;
  }
};